type RuntimeConfig = {
	apiUrl: string;
	backendOrigin: string;
	authorizeRedirectUri: string;
	passwordGrantClientId: string;
	authorizationCodeGrantClientId: string;
};

// Homolog usa domínios diferentes entre frontend e backend (.tec.br vs .com.br) —
// única exceção à convenção "client.<tenant> -> <tenant>" usada em produção.
const HOSTNAME_OVERRIDES: Record<string, string> = {
	"client.homol.sintegrada.tec.br": "homol.sintegrada.com.br",
};

function deriveApiOrigin(): string {
	// Override manual pra dev local (yarn start), onde não existe subdomínio
	// "client.<tenant>" nenhum e o backend roda numa porta arbitrária.
	if (process.env.REACT_APP_API_URL) {
		return process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "");
	}

	const { hostname, protocol } = window.location;

	if (hostname in HOSTNAME_OVERRIDES) {
		return `${protocol}//${HOSTNAME_OVERRIDES[hostname]}`;
	}

	if (!hostname.startsWith("client.")) {
		throw new Error(
			`Hostname "${hostname}" não segue a convenção "client.<tenant>" nem tem override configurado — não dá pra deduzir o backend. Defina REACT_APP_API_URL para desenvolvimento local.`
		);
	}

	return `${protocol}//${hostname.replace(/^client\./, "")}`;
}

let cachedConfig: RuntimeConfig | undefined;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
	if (cachedConfig) {
		return cachedConfig;
	}

	const backendOrigin = deriveApiOrigin();
	const apiUrl = `${backendOrigin}/api`;
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), 8000);
	let response: Response;

	try {
		response = await fetch(`${apiUrl}/newui/config`, { signal: controller.signal });
	} finally {
		clearTimeout(timeoutId);
	}

	if (!response.ok) {
		throw new Error(`Falha ao carregar configuração do tenant (status ${response.status})`);
	}

	const data = await response.json();

	if (!data?.password_grant_client_id || !data?.authorization_code_grant_client_id) {
		throw new Error("Configuração do tenant incompleta — client IDs do NewUI não vieram preenchidos.");
	}

	cachedConfig = {
		apiUrl,
		backendOrigin,
		authorizeRedirectUri: `${window.location.origin}/authorize-login`,
		passwordGrantClientId: data.password_grant_client_id,
		authorizationCodeGrantClientId: data.authorization_code_grant_client_id,
	};

	return cachedConfig;
}

export function getRuntimeConfig(): RuntimeConfig {
	if (!cachedConfig) {
		throw new Error(
			"Runtime config ainda não carregada — chame loadRuntimeConfig() antes de renderizar a aplicação."
		);
	}

	return cachedConfig;
}
